import { Button } from '@/components/ui/button';
import type { ChatRoomCredentials } from '@matter_extractor/shared';
import { useState } from 'react';
import {
  COPY_LINK_DONE,
  COPY_LINK_LABEL,
  CREATED_PANEL_DISMISS,
  CREATED_PANEL_TITLE,
  CREATED_PANEL_WARNING,
  GUEST_LINK_LABEL,
  INTERVIEWER_LINK_LABEL,
  chatLinkFor,
} from './entretien-labels';

/**
 * The two links of a freshly struck room - the ONLY place they are ever
 * readable (chat spec §12).
 *
 * ## Why this component is written the way it is
 *
 * There is no route that can serve these two strings again. They are columns of
 * `chat_rooms`, no read projection carries them, and the creation response is
 * the one response in the repo that does (§12). So this panel is not a
 * convenience: an operator who closes it without copying has destroyed a room,
 * and the only repair is to strike another one.
 *
 * Three consequences, all deliberate:
 *
 * - **It says so, in the panel.** The screen already carries the same sentence
 *   above its table, so this one arrives as a reminder rather than as news - but
 *   it has to be here too, at the moment the strings are in front of the eye.
 * - **It is dismissed by a gesture that names what it asserts** («  J'ai copié
 *   les deux liens ») rather than by an `×`. A cross is a control one clicks to
 *   get on with things; this one is an acknowledgement.
 * - **The full URL is shown, not the token.** What gets pasted into a message is
 *   a link, and an operator who had to assemble one from a token would assemble
 *   it wrong once - and a wrong link is indistinguishable from an expired one on
 *   the other side (§5: expired and unknown render the same screen).
 *
 * The two links are indistinguishable to the eye by design, which protects the
 * blind protocol from one pasted into the wrong window - so each is LABELLED,
 * and the labels are the only thing telling them apart.
 */

export interface RoomLinksPanelProps {
  room: ChatRoomCredentials;
  /** The origin the links are built on - the window's, injected for the test. */
  origin: string;
  onDismiss: () => void;
}

export function RoomLinksPanel({ room, origin, onDismiss }: RoomLinksPanelProps) {
  return (
    <section
      data-testid="chat-room-links"
      /*
        `bleu-nuit-tint` and not `jaune-tint`: this is not a warning about
        something that went wrong, it is the result of a gesture that succeeded -
        and the room reference beside it is what the operator came for. The
        sentence inside carries the urgency; the surface does not have to.
      */
      className="space-y-4 rounded-md border-2 border-bleu-nuit bg-bleu-nuit-tint p-5"
    >
      <div className="space-y-1">
        <h2 className="font-bold text-bleu-nuit text-lg">
          {CREATED_PANEL_TITLE} <span className="tabular-nums tracking-wide">{room.reference}</span>
        </h2>
        <p className="max-w-[80ch] font-semibold text-bleu-nuit-deep text-sm">
          {CREATED_PANEL_WARNING}
        </p>
      </div>
      <div className="space-y-3">
        <LinkRow
          label={INTERVIEWER_LINK_LABEL}
          url={chatLinkFor(origin, room.interviewerToken)}
          testId="chat-link-interviewer"
        />
        <LinkRow
          label={GUEST_LINK_LABEL}
          url={chatLinkFor(origin, room.guestToken)}
          testId="chat-link-guest"
        />
      </div>
      <Button type="button" variant="outline" size="sm" onClick={onDismiss}>
        {CREATED_PANEL_DISMISS}
      </Button>
    </section>
  );
}

function LinkRow({ label, url, testId }: { label: string; url: string; testId: string }) {
  /**
   * Whether THIS row has been copied.
   *
   * Per row and not per panel: the two links go to two different people, and a
   * single « Copié » would let somebody send the same link twice - which is the
   * one mistake that breaks the blind protocol without looking like a mistake,
   * since the two strings are indistinguishable to the eye (§5).
   */
  const [copied, setCopied] = useState(false);
  return (
    <div className="space-y-1">
      <p className="font-semibold text-bleu-nuit-deep text-xs uppercase tracking-wide">{label}</p>
      <div className="flex flex-wrap items-center gap-2">
        {/*
          A read-only input rather than a `<p>`: it is selectable with a
          double-click and with a keyboard, which is the fallback for every
          browser and permission state where the clipboard API is refused. The
          copy button below is the affordance; this is what makes the panel work
          without it.
        */}
        <input
          data-testid={testId}
          readOnly
          value={url}
          onFocus={(event) => event.currentTarget.select()}
          className="min-w-0 flex-1 rounded-md border border-gray-200 bg-white px-3 py-2 font-mono text-gray-900 text-xs"
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            // `void`: a clipboard refused by the browser must not take the
            // panel down with it - the input beside the button is still
            // selectable, which is why the failure is survivable in silence
            // here and is NOT elsewhere on this screen.
            void navigator.clipboard
              ?.writeText(url)
              .then(() => setCopied(true))
              .catch(() => undefined);
          }}
        >
          {copied ? COPY_LINK_DONE : COPY_LINK_LABEL}
        </Button>
      </div>
    </div>
  );
}
